import React from 'react';
import { NativeSyntheticEvent, TextInputKeyPressEventData, View } from 'react-native';
import { useState } from 'react';
import MyDataTable from '../../../components/MyDataTable';
import { translate } from '../../../services/translate';
import { Pagination } from '../../../components/Pagination';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { GuardianAppParamList } from '../../../typings/Params';
import { guardianAppService } from '../../../services/central-api/guardian-app';
import EditModal from './EditModal';
import DeleteModal from '../../../components/DeleteModal';
import Toast from 'react-native-toast-message';
import { ClientError, GenericPaginatedParameters } from '../../../services/central-api/base-service';
import { TextMask } from 'react-native-masked-text';
import FilterInput from '../../../components/Filter/components/FilterInput';
import CustomButton from '../../../components/CustomButton';
import DropdownButton from '../../../components/DropdownButton';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import { useQuery } from '@tanstack/react-query';

interface Props {
    navigation: Navigation;
}

export default function GuardianAppUsers({ navigation }: Props) {
    const { theme } = useStyles(styleSheet);

    const [selectedGuardianWoman, setSelectedGuardianWoman] = useState<number | null>(null);
    const [isModalVisible, setModalVisible] = useState<boolean>(false);
    const [selectedGuardianWomanToDelete, setSelectedGuardianWomanToDelete] = useState<GuardianWoman | null>(null);
    const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);

    const route = useRoute<RouteProp<GuardianAppParamList, 'GuardianAppUsers'>>();

    const [filters, setFilters] = useState<GenericPaginatedParameters>({
        page: Math.max(route.params.page - 1, 0),
        textFilter: route.params.textFilter ?? '',
        limit: Number(route.params.limit) || 25,
    });
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');

    const isFocused = useIsFocused();
    const { data, isLoading, refetch } = useQuery({
        queryKey: ['getPaginatedGuardianWoman', filters],
        queryFn: () => guardianAppService.getPaginatedGuardianWoman(filters),
        enabled: isFocused,
    });

    async function confirmDelete(guardianWoman: GuardianWoman) {
        try {
            await guardianAppService.deleteGuardianWoman(guardianWoman.id);
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            setDeleteModalVisible(false);
            refetch();
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setSelectedGuardianWoman(null);
        }
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }

    return <Container>
        <Header
            title={translate('users')}
            actions={<CustomButton
                icon={faPlus}
                onPress={() => setModalVisible(true)}
                text={translate('create')}
            />}
        />
        <Card>
            <DataTableHeader
                title={translate('registeredGuardianAppPeople')}
                zIndex={2}
                filterInput={<FilterInput
                    value={textFilter ?? ''}
                    onChange={text => {
                        setTextFilter(text);
                    }}
                    placeholder={translate('typeToSearch')}
                    onKeyPress={handleKeyDown}
                />}
            />
            <DataTableContainer
                dataTable={<MyDataTable
                    progressPending={isLoading}
                    columns={[{
                        name: 'ID',
                        selector: row => row.id,
                        grow: 0
                    },
                    {
                        name: translate('phone'),
                        cell: row => <View style={{ width: '100%' }}>
                            <TextMask
                                style={{ color: theme.colors.textColor, fontFamily: 'Open Sans' }}
                                type={'cel-phone'}
                                value={row.phone ?? undefined} />
                        </View>,
                        wrap: true,
                        minWidth: '150px',
                        maxWidth: '150px'
                    },
                    {
                        name: translate('name'),
                        selector: row => row.name ?? '',
                        wrap: true,
                    },
                    {
                        name: translate('document'),
                        selector: row => row.document ? row.document.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4') : '',
                        wrap: true,
                    },
                    {
                        name: translate('address'),
                        selector: row => row.address ?? '',
                        wrap: true,
                    },
                    {
                        button: true,
                        cell: row =>
                            <DropdownButton
                                actions={[
                                    {
                                        content: translate('editInformation'),
                                        onPress: () => {
                                            setSelectedGuardianWoman(row.id);
                                            setModalVisible(true);
                                        }
                                    },
                                    {
                                        content: translate('delete'),
                                        variant: 'danger',
                                        onPress: () => {
                                            setSelectedGuardianWomanToDelete(row);
                                            setDeleteModalVisible(true);
                                        },
                                    }
                                ]}
                            />
                    }]}
                    data={data?.rows || []}
                />}
                pagination={<Pagination
                    totalItems={data?.count || 0}
                    currentPage={filters.page}
                    pageSize={filters.limit}
                    setPageSize={pageSize => {
                        setFilters(old => ({
                            ...old,
                            limit: pageSize,
                            page: 0,
                        }));

                        navigation.setParams({
                            ...route.params,
                            limit: pageSize,
                            page: 1,
                        });
                    }}
                    setPage={page => {
                        setFilters(old => ({
                            ...old,
                            page
                        }));

                        navigation.setParams({
                            ...route.params,
                            page: page + 1
                        });
                    }}
                />}
            />
        </Card>
        <EditModal
            guardianWomanId={selectedGuardianWoman}
            isModalVisible={isModalVisible}
            setModalVisible={setModalVisible}
            onSubmit={async () => {
                await refetch();
                setSelectedGuardianWoman(null);
            }}
            onClose={() => {
                setSelectedGuardianWoman(null);
            }}
        />

        <DeleteModal
            setModalVisible={setDeleteModalVisible}
            isModalVisible={isDeleteModalVisible}
            itemName={selectedGuardianWomanToDelete?.name || ''}
            onSubmit={async () => {
                if (selectedGuardianWomanToDelete) {
                    await confirmDelete(selectedGuardianWomanToDelete);
                }
            }}
        />
    </Container>;
}

const styleSheet = createStyleSheet(() => ({}));
